if (!customElements.get('product-quiz')) {
    customElements.define(
        'product-quiz',
        class ProductQuiz extends HTMLElement {
            constructor() {
                super();

                this.collectionHandle = this.dataset.collectionHandle;
                this.currentQuestion = 0;
                this.answers = {};
                this.products = [];

                // Get all questions
                this.questions = Array.from(this.querySelectorAll('.quiz-question'));
                this.totalQuestions = this.questions.length;

                // Get UI elements
                this.screens = {
                    start: this.querySelector('[data-screen="start"]'),
                    questions: this.querySelector('[data-screen="questions"]'),
                    results: this.querySelector('[data-screen="results"]')
                };

                this.progressBar = this.querySelector('[data-quiz-progress]');
                this.currentQuestionEl = this.querySelector('[data-current-question]');
                this.totalQuestionsEl = this.querySelector('[data-total-questions]');
                this.backButton = this.querySelector('[data-quiz-back]');
                this.resultsContainer = this.querySelector('[data-quiz-results]');

                this.init();
            }

            init() {
                // Start button
                const startButton = this.querySelector('[data-quiz-start]');
                if (startButton) {
                    startButton.addEventListener('click', () => this.startQuiz());
                }

                // Answer buttons
                this.querySelectorAll('.quiz-answer').forEach(button => {
                    button.addEventListener('click', (e) => this.handleAnswer(e));
                });

                // Back button
                if (this.backButton) {
                    this.backButton.addEventListener('click', () => this.goBack());
                }

                // Restart button
                const restartButton = this.querySelector('[data-quiz-restart]');
                if (restartButton) {
                    restartButton.addEventListener('click', () => this.restartQuiz());
                }

                // Load products from collection
                this.loadProducts();
            }

            async loadProducts() {
                if (!this.collectionHandle) {
                    console.warn('No collection handle provided for quiz');
                    return;
                }

                try {
                    const response = await fetch(`/collections/${this.collectionHandle}/products.json?limit=250`);
                    const data = await response.json();
                    this.products = data.products;
                } catch (error) {
                    console.error('Error loading products:', error);
                }
            }

            startQuiz() {
                this.showScreen('questions');
                this.currentQuestion = 0;
                this.answers = {};
                this.showQuestion(0);
                this.updateProgress();
            }

            showScreen(screenName) {
                Object.values(this.screens).forEach(screen => {
                    screen.classList.remove('active');
                });

                if (this.screens[screenName]) {
                    setTimeout(() => {
                        this.screens[screenName].classList.add('active');
                    }, 100);
                }
            }

            showQuestion(index) {
                // Hide all questions
                this.questions.forEach((q, i) => {
                    if (i === this.currentQuestion && i !== index) {
                        q.classList.add('exiting');
                        setTimeout(() => {
                            q.classList.remove('active', 'exiting');
                        }, 300);
                    } else {
                        q.classList.remove('active');
                    }
                });

                // Show current question
                setTimeout(() => {
                    if (this.questions[index]) {
                        this.questions[index].classList.add('active');
                    }
                }, 300);

                this.currentQuestion = index;
                this.updateProgress();
                this.updateBackButton();
            }

            handleAnswer(event) {
                const button = event.currentTarget;
                const question = button.closest('.quiz-question');
                const questionIndex = parseInt(question.dataset.question) - 1;
                const answerValue = button.dataset.answerValue;
                const metafieldNamespace = question.dataset.metafieldNamespace;
                const metafieldKey = question.dataset.metafieldKey;

                // Store answer
                this.answers[questionIndex] = {
                    value: answerValue,
                    namespace: metafieldNamespace,
                    key: metafieldKey
                };

                // Visual feedback
                question.querySelectorAll('.quiz-answer').forEach(btn => {
                    btn.classList.remove('selected');
                });
                button.classList.add('selected');

                // Move to next question or show results
                setTimeout(() => {
                    if (this.currentQuestion < this.totalQuestions - 1) {
                        this.showQuestion(this.currentQuestion + 1);
                    } else {
                        this.showResults();
                    }
                }, 400);
            }

            goBack() {
                if (this.currentQuestion > 0) {
                    this.showQuestion(this.currentQuestion - 1);
                }
            }

            updateProgress() {
                const progress = ((this.currentQuestion + 1) / this.totalQuestions) * 100;
                if (this.progressBar) {
                    this.progressBar.style.width = `${progress}%`;
                }

                if (this.currentQuestionEl) {
                    this.currentQuestionEl.textContent = this.currentQuestion + 1;
                }
            }

            updateBackButton() {
                if (this.backButton) {
                    if (this.currentQuestion > 0) {
                        this.backButton.removeAttribute('hidden');
                    } else {
                        this.backButton.setAttribute('hidden', 'true');
                    }
                }
            }

            async showResults() {
                this.showScreen('results');

                // Filter products based on answers
                const matchedProducts = this.filterProducts();

                // Display results
                await this.displayResults(matchedProducts);
            }

            filterProducts() {
                if (!this.products || this.products.length === 0) {
                    return [];
                }

                const scoredProducts = this.products.map(product => {
                    let matchScore = 0;
                    let totalCriteria = 0;

                    // Check each answer against product metafields
                    Object.values(this.answers).forEach(answer => {
                        totalCriteria++;

                        // Get metafield value from product
                        const metafieldValue = this.getProductMetafield(
                            product,
                            answer.namespace,
                            answer.key
                        );

                        if (metafieldValue) {
                            // Normalize values for comparison
                            const normalizedMetafield = String(metafieldValue).toLowerCase().trim();
                            const normalizedAnswer = String(answer.value).toLowerCase().trim();

                            // Check for exact match or contains
                            if (normalizedMetafield === normalizedAnswer ||
                                normalizedMetafield.includes(normalizedAnswer)) {
                                matchScore++;
                            }
                        }
                    });

                    return {
                        product,
                        matchScore,
                        matchPercentage: totalCriteria > 0 ? (matchScore / totalCriteria) * 100 : 0
                    };
                });

                // Sort by match score (highest first)
                scoredProducts.sort((a, b) => b.matchScore - a.matchScore);

                // Filter products with at least some matches
                const minMatchPercentage = 30; // At least 30% match
                return scoredProducts.filter(item => item.matchPercentage >= minMatchPercentage);
            }

            getProductMetafield(product, namespace, key) {
                // Try to get metafield from product
                if (product.metafields && product.metafields[namespace]) {
                    return product.metafields[namespace][key];
                }

                // Try alternative metafield structure
                if (product[`${namespace}_${key}`]) {
                    return product[`${namespace}_${key}`];
                }

                // Try tags as fallback
                if (product.tags && Array.isArray(product.tags)) {
                    const tagPrefix = `${key}:`;
                    const matchingTag = product.tags.find(tag =>
                        tag.toLowerCase().startsWith(tagPrefix.toLowerCase())
                    );
                    if (matchingTag) {
                        return matchingTag.split(':')[1]?.trim();
                    }
                }

                return null;
            }

            async displayResults(matchedProducts) {
                if (!this.resultsContainer) return;

                // Get max results from section settings
                const sectionSettings = this.closest('.shopify-section');
                const maxResults = sectionSettings?.dataset?.maxResults || 6;

                // Limit results
                const productsToShow = matchedProducts.slice(0, parseInt(maxResults));

                if (productsToShow.length === 0) {
                    this.resultsContainer.innerHTML = `
            <div class="quiz-no-results">
              <div class="quiz-no-results-icon">🔍</div>
              <h4 class="heading text-xl mb-2">No encontramos productos que coincidan</h4>
              <p class="text-opacity mb-4">Intenta responder el quiz de nuevo con diferentes opciones.</p>
            </div>
          `;
                    return;
                }

                // Create products grid
                const gridHTML = `
          <div class="quiz-results-grid">
            ${productsToShow.map((item, index) => this.createProductCard(item, index)).join('')}
          </div>
        `;

                this.resultsContainer.innerHTML = gridHTML;
            }

            createProductCard(item, index) {
                const { product, matchPercentage } = item;
                const productUrl = `/products/${product.handle}`;
                const imageUrl = product.images && product.images[0]
                    ? product.images[0].src
                    : '';
                const price = product.variants && product.variants[0]
                    ? this.formatMoney(product.variants[0].price)
                    : '';

                return `
          <div class="product-card card flex flex-col">
            ${matchPercentage >= 80 ? `<div class="quiz-match-score">${Math.round(matchPercentage)}% Match</div>` : ''}
            <a href="${productUrl}" class="product-card__media block relative">
              <div class="media media--square">
                ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" loading="lazy" class="w-full h-full object-cover">` : ''}
              </div>
            </a>
            <div class="product-card__content p-4 flex flex-col grow">
              <h3 class="product-card__title text-base font-medium mb-2">
                <a href="${productUrl}" class="reversed-link">${product.title}</a>
              </h3>
              ${price ? `<div class="product-card__price font-bold">${price}</div>` : ''}
              <a href="${productUrl}" class="button button--primary w-full mt-4" is="hover-link">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">Ver Producto</span>
              </a>
            </div>
          </div>
        `;
            }

            formatMoney(cents) {
                if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
                    return Shopify.formatMoney(cents, theme.moneyFormat || '${{amount}}');
                }

                const dollars = (cents / 100).toFixed(2);
                return `$${dollars}`;
            }

            restartQuiz() {
                this.currentQuestion = 0;
                this.answers = {};

                // Reset all answer selections
                this.querySelectorAll('.quiz-answer').forEach(btn => {
                    btn.classList.remove('selected');
                });

                this.showScreen('start');
            }
        }
    );
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('product-quiz').forEach(element => {
        if (!element.initialized) {
            element.initialized = true;
        }
    });
});
